import {Dropdown as PepsicoDsDropdown} from '@pepsico-ds/ui'
import PropTypes from 'prop-types'
/**
 * Dropdown component that renders a dropdown menu with various customization options.
 *
 * @param {Object} props - The properties object.
 * @param {any} props.value - The current value of the dropdown.
 * @param {Function} props.updateValue - Function to update the value of the dropdown.
 * @param {string} [props.label=''] - The label for the dropdown.
 * @param {Object} props.schema - The schema object containing options and other configurations.
 * @param {Object} props.uiSchema - The UI schema object containing UI-specific configurations.
 * @param {boolean} [props.required=false] - Indicates if the dropdown is required.
 * @param {boolean} [props.isError=false] - Indicates if there is an error.
 * @param {boolean} [props.isJsonWrap=true] - Determines if the dropdown should use JSON wrapping.
 * @param {Array} [props.childList=[]] - List of child elements for the dropdown.
 * @param {string} [props.dropdownHeader=''] - The header for the dropdown.
 * @param {string} [props.className=''] - Additional class names for the dropdown.
 * @param {boolean} [props.isDark=true] - Indicates if the dropdown should use a dark theme.
 * @param {('single'|'multiple')} [props.selection='single'] - The selection mode for the dropdown.
 * @param {('small'|'medium')} [props.size='medium'] - The size of the dropdown.
 * @param {string} [props.placeholder=''] - The placeholder text for the dropdown.
 * @param {string} [props.helperText=''] - The helper text for the dropdown.
 * @param {string} [props.errorMessage=''] - The error message for the dropdown.
 * @param {boolean} [props.isClearable=true] - Indicates if the dropdown can be cleared.
 * @returns {JSX.Element} The rendered Dropdown component.
 */
const toDropdownItems = (list = []) =>
  list.map(item => ({
    id: item.value ?? item.id ?? '',
    displayText: item.label ?? item.displayText ?? '',
    ...item,
  }))

const toSelectedItems = (value, list = []) => {
  if (value == null || value === '') return []
  const values = Array.isArray(value) ? value : [value]
  return values
    .map(v => list.find(item => (item.value ?? item.id) === v))
    .filter(Boolean)
    .map(item => ({
      id: item.value ?? item.id ?? '',
      displayText: item.label ?? item.displayText ?? '',
      ...item,
    }))
}

const fromSelectedItems = (selectedItems, isMultiple) => {
  if (!selectedItems || selectedItems.length === 0) return isMultiple ? [] : ''
  const ids = selectedItems.map(item => item.id)
  return isMultiple ? ids : ids[0]
}

function Dropdown({
  value,
  updateValue,
  label = '',
  schema,
  uiSchema,
  required = false,
  isError = false,
  isJsonWrap = true,
  childList = [],
  dropdownHeader = '',
  className = '',
  isDark = true,
  selection = 'single',
  size = 'medium',
  placeholder = '',
  helperText = '',
  errorMessage = '',
  isClearable = true,
}) {
  const dropdownId = `dropdown-${label.replace(/\s+/g, '-').toLowerCase()}`

  const getIsError = () => uiSchema.value === '' || isError;

  const getHelperText = () => {
    const hasError = getIsError()
    if (hasError) {
      return uiSchema?.errorMessage
    }
    return helperText
  }

  return (
    <>
      {isJsonWrap ? (
        <PepsicoDsDropdown
          id={dropdownId}
          isRequired={required}
          childList={toDropdownItems(schema.options)}
          dropdownHeader={schema.header}
          style={{minWidth: '0px'}}
          className={uiSchema?.className || ''}
          isDark={uiSchema?.isDark || ''}
          label={label}
          error={getIsError()}
          helperText={getHelperText()}
          selection={uiSchema.selection || 'single'}
          selectedValue={toSelectedItems(value, schema.options)}
          setSelectedValue={selectedItems =>
            updateValue(fromSelectedItems(selectedItems, (uiSchema.selection || 'single') === 'multiple'))
          }
          size={uiSchema.size || 'medium'}
          placeholder={uiSchema.placeholder || ''}
          isClearable={isClearable}
        />
      ) : (
        <PepsicoDsDropdown
          id={dropdownId}
          childList={toDropdownItems(childList)}
          dropdownHeader={dropdownHeader}
          style={{minWidth: '0px'}}
          className={className || ''}
          isDark={isDark || ''}
          label={label}
          error={isError}
          selection={selection}
          selectedValue={toSelectedItems(value, childList)}
          setSelectedValue={selectedItems =>
            updateValue(fromSelectedItems(selectedItems, selection === 'multiple'))
          }
          size={size}
          placeholder={placeholder}
          isRequired={required}
          helperText={isError ? errorMessage : helperText}
          isClearable={isClearable}
        />
      )}
    </>
  )
}

Dropdown.propTypes = {
  value: PropTypes.any,
  updateValue: PropTypes.func.isRequired,
  label: PropTypes.string,
  schema: PropTypes.shape({
    options: PropTypes.array,
    header: PropTypes.string,
  }),
  uiSchema: PropTypes.shape({
    value: PropTypes.any,
    errorMessage: PropTypes.string,
    className: PropTypes.string,
    isDark: PropTypes.bool,
    selection: PropTypes.string,
    size: PropTypes.string,
    placeholder: PropTypes.string,
  }),
  required: PropTypes.bool,
  isError: PropTypes.bool,
  isJsonWrap: PropTypes.bool,
  childList: PropTypes.array,
  dropdownHeader: PropTypes.string,
  className: PropTypes.string,
  isDark: PropTypes.bool,
  selection: PropTypes.string,
  size: PropTypes.string,
  placeholder: PropTypes.string,
  helperText: PropTypes.string,
  errorMessage: PropTypes.string,
  isClearable: PropTypes.bool,
}

export default Dropdown
